import { defineStore } from "pinia"
import type { DevTicket, DevTicketPriority, DevTicketStatus } from "~/models/dev-ticket"
import type { ApiResponse } from "~/types/api"

export type DevTicketStatusType = {
	name: string
	value: string
	borderClass: string
	bgClass: string
	txtClass: string
}

// dev ticket :: create dev ticket
export interface CreateDevTicketRequest {
	title: string
	priority: DevTicketPriority
}
export interface CreateDevTicketResponse extends ApiResponse {
	data: DevTicket
}
const CreateDevTicket = {
	method: "POST" as const,
	url: () => `dev-tickets/create`
}

// dev ticket :: edit dev ticket
export interface EditDevTicketRequest {
	id: number
	status: DevTicketStatus
}
export interface EditDevTicketResponse extends ApiResponse {
	data: DevTicket
}
const EditDevTicket = {
	method: "POST" as const,
	url: () => `dev-tickets/edit`
}

// dev ticket :: delete dev ticket
export interface DeleteDevTicketRequest {}
export interface DeleteDevTicketResponse extends ApiResponse {
	data: null
}
const DeleteDevTicket = {
	method: "POST" as const,
	url: (id: number) => `dev-tickets/delete/${id}`
}

// dev ticket :: get dev ticket
export interface GetDevTicketRequest {}
export interface GetDevTicketResponse extends ApiResponse {
	data: null
}
const GetDevTicket = {
	method: "GET" as const,
	url: (id: number) => `dev-tickets/get/${id}`
}

// dev ticket :: search dev tickets
export interface SearchDevTicketsRequest {
	status?: DevTicketStatus
	statuses?: string
	page?: number
	limit?: number
	search?: string
	order_by?: "created" | "updated"
	direction?: "asc" | "desc"
}
export interface SearchDevTicketsResponse extends ApiResponse {
	data: {
		tickets: DevTicket[]
		total: number
	}
}
const SearchDevTickets = {
	method: "GET" as const,
	url: () => `dev-tickets/search`
}

// dev ticket :: create dev ticket message
export interface CreateDevTicketMessageRequest {
	ticket_id: number
	message: string
	image_id?: number | null
	video_id?: number | null
}
export interface CreateDevTicketMessageResponse extends ApiResponse {
	data: DevTicket
}
const CreateDevTicketMessage = {
	method: "POST" as const,
	url: () => `dev-tickets/messages/create`
}

// dev ticket :: delete dev ticket message
export interface DeleteDevTicketMessageRequest {}
export interface DeleteDevTicketMessageResponse extends ApiResponse {
	data: DevTicket
}
const DeleteDevTicketMessage = {
	method: "POST" as const,
	url: (id: number) => `dev-tickets/messages/delete/${id}`
}

export const useDevTicketApiUrl = () => ({
	CreateDevTicket,
	EditDevTicket,
	DeleteDevTicket,
	GetDevTicket,
	SearchDevTickets,
	CreateDevTicketMessage,
	DeleteDevTicketMessage
})

export const useDevTicketStore = defineStore("dev-ticket", () => {
	const { $i18n } = useNuxtApp()

	const state = {
		statuses: ref<DevTicketStatusType[]>([
			{
				name: $i18n.t("dev_ticket_status_dev_pending"),
				value: "dev-pending",
				borderClass: "border-yellow-500",
				bgClass: "bg-yellow-500",
				txtClass: "text-yellow-500"
			},
			{
				name: $i18n.t("dev_ticket_status_user_pending"),
				value: "user-pending",
				borderClass: "border-purple-500",
				bgClass: "bg-purple-500",
				txtClass: "text-purple-500"
			},
			{
				name: $i18n.t("dev_ticket_status_processing"),
				value: "processing",
				borderClass: "border-blue-500",
				bgClass: "bg-blue-500",
				txtClass: "text-blue-500"
			},
			{
				name: $i18n.t("dev_ticket_status_dev_finished"),
				value: "finished",
				borderClass: "border-green-500",
				bgClass: "bg-green-500",
				txtClass: "text-green-500"
			}
		]),
		priorities: ref<DevTicketStatusType[]>([
			{
				name: $i18n.t("low"),
				value: "low",
				borderClass: "border-blue-500",
				bgClass: "bg-blue-500",
				txtClass: "text-blue-500"
			},
			{
				name: $i18n.t("medium"),
				value: "medium",
				borderClass: "border-yellow-500",
				bgClass: "bg-yellow-500",
				txtClass: "text-yellow-500"
			},
			{
				name: $i18n.t("high"),
				value: "high",
				borderClass: "border-red-500",
				bgClass: "bg-red-500",
				txtClass: "text-red-500"
			}
		])
	}

	const createDevTicket = async (formData: CreateDevTicketRequest) => {
		const data = await $fetch<CreateDevTicketResponse>(CreateDevTicket.url(), {
			...useFetchConfig(),
			method: CreateDevTicket.method,
			body: formData
		})
		return data.data
	}

	const editDevTicket = async (formData: EditDevTicketRequest) => {
		const data = await $fetch<EditDevTicketResponse>(EditDevTicket.url(), {
			...useFetchConfig(),
			method: EditDevTicket.method,
			body: formData
		})
		return data.data
	}

	const deleteDevTicket = async (ticketId: number) => {
		const data = await $fetch<DeleteDevTicketResponse>(DeleteDevTicket.url(ticketId), {
			...useFetchConfig(),
			method: DeleteDevTicket.method
		})
		return data.data
	}

	const getDevTicket = async (ticketId: number) => {
		const data = await $fetch<GetDevTicketResponse>(GetDevTicket.url(ticketId), {
			...useFetchConfig(),
			method: GetDevTicket.method
		})
		return data.data
	}

	const searchDevTickets = async (formData: SearchDevTicketsRequest) => {
		const data = await $fetch<SearchDevTicketsResponse>(SearchDevTickets.url(), {
			...useFetchConfig(),
			method: SearchDevTickets.method,
			query: formData
		})
		return data.data
	}

	const createDevTicketMessage = async (formData: CreateDevTicketMessageRequest) => {
		const data = await $fetch<CreateDevTicketMessageResponse>(CreateDevTicketMessage.url(), {
			...useFetchConfig(),
			method: CreateDevTicketMessage.method,
			body: formData
		})
		return data.data
	}

	const deleteDevTicketMessage = async (messageId: number) => {
		const data = await $fetch<DeleteDevTicketMessageResponse>(DeleteDevTicketMessage.url(messageId), {
			...useFetchConfig(),
			method: DeleteDevTicketMessage.method
		})
		return data.data
	}

	return {
		...state,
		createDevTicket,
		editDevTicket,
		deleteDevTicket,
		getDevTicket,
		searchDevTickets,
		createDevTicketMessage,
		deleteDevTicketMessage
	}
})
